// src/utils/printReceipt.js
// Shared print utility used by POS (80mm) and DocumentForm (A4)
import { tafqeetUSD, tafqeetLBP } from './tafqeet';
import api, { isLocalInstall } from '../config/api';

const money  = (n) => Number(n || 0).toFixed(2);
const lbpFmt = (n) => Math.round(Number(n || 0)).toLocaleString('en-US');

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

// Build absolute logo URL from a relative path like /uploads/foo.jpg
// Mirrors the logic in config/api.js resolveMediaUrl
const _API_BASE =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : (window.location.port === '3000' ? 'http://localhost:3001' : '');

function resolveLogoUrl(logoUrl) {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  return `${_API_BASE}${logoUrl}`;
}

// ─── 80mm Thermal Receipt (POS) ───────────────────────────────────────────────
export function print80mm({ company, items, totals, paidUSD, showLBP, date, docNumber, rate }) {
  const logoSrc = resolveLogoUrl(company?.logo_url);

  const rows = items.map((r) => `
    <tr>
      <td class="name">${escapeHtml(r.name)}${r.variantName ? `<br><span class="sub">${escapeHtml(r.variantName)}</span>` : ''}${r.serial ? `<br><span class="sub" style="font-family:monospace">S/N: ${escapeHtml(r.serial)}</span>` : ''}</td>
      <td class="center">${r.qty ?? r.quantity}</td>
      <td class="right">${showLBP ? lbpFmt(r.priceLBP ?? (r.priceUSD * rate)) : money(r.priceUSD ?? r.price)}</td>
      <td class="right">${showLBP ? lbpFmt((r.priceLBP ?? (r.priceUSD * rate)) * (r.qty ?? r.quantity)) : money((r.priceUSD ?? r.price) * (r.qty ?? r.quantity))}</td>
    </tr>`).join('');

  const subtotalUSD = items.reduce((s, r) => s + (r.priceUSD ?? r.price) * (r.qty ?? r.quantity), 0);
  const discountUSD = Number(totals?.discount || 0);
  const vatUSD      = Number(totals?.vat || 0);
  const vatRate     = Number(totals?.vatRate || 0);
  const totalUSD    = totals?.total ?? (subtotalUSD - discountUSD);
  const totalLBP    = totals?.totalLBP ?? Math.round(totalUSD * rate);
  const balance     = totalUSD - (paidUSD || 0);

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Receipt ${escapeHtml(String(docNumber || ''))}</title>
<style>
  * { font-family: 'Courier New', monospace; font-size: 12px; margin: 0; padding: 0; }
  body { width: 72mm; margin: 0 auto; padding: 4mm; }
  .center { text-align: center; }
  .right  { text-align: right; }
  .name   { text-align: left; max-width: 30mm; word-break: break-word; }
  .sub    { font-size: 10px; color: #555; }
  .logo   { display: block; max-width: 40mm; max-height: 20mm; margin: 0 auto 4px; object-fit: contain; }
  h2      { text-align: center; font-size: 14px; margin: 4px 0 2px; }
  .info   { text-align: center; font-size: 11px; color: #333; margin-bottom: 2px; }
  hr      { border: none; border-top: 1px dashed #000; margin: 4px 0; }
  table   { width: 100%; border-collapse: collapse; }
  th      { font-size: 10px; border-bottom: 1px solid #000; padding: 2px 0; }
  td      { padding: 2px 0; vertical-align: top; }
  .tot    { margin-top: 4px; }
  .tot-row{ display: flex; justify-content: space-between; padding: 1px 0; }
  .bold   { font-weight: bold; }
  .muted  { font-size: 10px; color: #555; }
  .footer { text-align: center; font-size: 11px; margin-top: 6px; }
</style></head>
<body>
  ${logoSrc ? `<img class="logo" src="${logoSrc}" alt="logo" />` : ''}
  <h2>${escapeHtml(company?.name || 'MMM POS')}</h2>
  ${company?.phone   ? `<div class="info">${escapeHtml(company.phone)}</div>` : ''}
  ${company?.address ? `<div class="info">${escapeHtml(company.address)}</div>` : ''}
  <hr/>
  <div class="info">Invoice: ${escapeHtml(String(docNumber || '—'))} &nbsp;|&nbsp; ${escapeHtml(date || '')}</div>
  <hr/>
  <table>
    <thead><tr>
      <th class="name">Item</th>
      <th class="center">Qty</th>
      <th class="right">Price</th>
      <th class="right">Total</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <hr/>
  <div class="tot">
    ${discountUSD > 0 ? `<div class="tot-row"><span>Subtotal</span><span>$${money(subtotalUSD)}</span></div>` : ''}
    ${discountUSD > 0 ? `<div class="tot-row" style="color:#dc2626"><span>Discount</span><span>−$${money(discountUSD)}</span></div>` : ''}
    ${showLBP ? `<div class="tot-row"><span>Total LBP</span><span>${lbpFmt(totalLBP)}</span></div>` : ''}
    <div class="tot-row bold"><span>Total USD</span><span>$${money(totalUSD)}</span></div>
    ${vatUSD > 0 ? `<div class="tot-row muted"><span>  incl. VAT ${vatRate}%</span><span>$${money(vatUSD)}</span></div>` : ''}
    <div class="tot-row"><span>Paid</span><span>$${money(paidUSD || 0)}</span></div>
    ${balance > 0.005 ? `<div class="tot-row bold"><span>Balance</span><span>$${money(balance)}</span></div>` : ''}
  </div>
  <hr/>
  ${totalUSD ? `<div class="center" style="font-size:10px;margin:3px 0;direction:rtl">${escapeHtml(tafqeetUSD(totalUSD))}</div><hr/>` : ''}
  <div class="footer">${escapeHtml(company?.receipt_footer || 'Thank you!')}</div>
  <script>window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 400); }</script>
</body></html>`;

  openPrint(html, 'receipt_80mm');
}

// ─── A4 Invoice (DocumentForm) ────────────────────────────────────────────────
export function printA4({ company, document: doc, items, totals, payments, accounts, showLBP, rate }) {
  const logoSrc = resolveLogoUrl(company?.logo_url);

  const DOC_TYPES = {
    100: 'Purchase In', 120: 'Purchase Return',
    200: 'Sales Out',   220: 'Sales Return',
    230: 'Proforma',    300: 'Inventory Count', 400: 'Loss/Damage',
  };

  const account = accounts?.find(a => String(a._id) === String(doc?.account_id));
  const docDate = doc?.date ? new Date(doc.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const docType = DOC_TYPES[doc?.type] || 'Document';

  const itemRows = items.map((item, i) => `
    <tr class="${i % 2 === 0 ? 'even' : ''}">
      <td>${escapeHtml(item.product_name || '')}${item.variant_name ? `<br><span class="sub">${escapeHtml(item.variant_name)}</span>` : ''}</td>
      <td class="center">${item.quantity}</td>
      <td class="right">$${money(item.price)}</td>
      ${item.tax    ? `<td class="right">$${money(item.tax)}</td>`    : ''}
      ${item.discount ? `<td class="right">$${money(item.discount)}</td>` : ''}
      <td class="right bold">$${money(item.total)}</td>
    </tr>`).join('');

  const hasTax      = items.some(i => i.tax > 0);
  const hasDiscount = items.some(i => i.discount > 0);

  const paymentRows = (payments || []).map(p => `
    <tr>
      <td>${escapeHtml(p.payment_method_name || '')}</td>
      <td>${new Date(p.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
      <td class="right">$${money(p.amount)}</td>
      <td>${escapeHtml(p.notes || '')}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${escapeHtml(doc?.number || 'Invoice')}</title>
<style>
  * { font-family: 'Segoe UI', Arial, sans-serif; box-sizing: border-box; margin: 0; padding: 0; }
  body { padding: 20mm 18mm; font-size: 12px; color: #111; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10mm; }
  .company-info h1 { font-size: 22px; font-weight: 700; color: #2e9f86; margin-bottom: 4px; }
  .company-info p  { font-size: 11px; color: #555; line-height: 1.6; }
  .logo { max-height: 25mm; max-width: 50mm; object-fit: contain; }
  .doc-info { background: #f8f9fa; border-radius: 8px; padding: 10px 14px; margin-bottom: 8mm; display: flex; justify-content: space-between; gap: 20px; }
  .doc-info-block { flex: 1; }
  .doc-info-block .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #9ca3af; margin-bottom: 2px; }
  .doc-info-block .value { font-size: 13px; font-weight: 600; }
  .doc-number { font-size: 18px; font-weight: 700; color: #2e9f86; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 6mm; font-size: 12px; }
  thead tr { background: #2e9f86; color: #fff; }
  thead th { padding: 8px 10px; text-align: left; font-weight: 600; font-size: 11px; }
  thead th.right, td.right { text-align: right; }
  thead th.center, td.center { text-align: center; }
  tbody tr.even { background: #f8f9fa; }
  tbody td { padding: 7px 10px; border-bottom: 1px solid #f0f0f0; }
  .sub { font-size: 10px; color: #6b7280; }
  .bold { font-weight: 700; }
  .totals-section { display: flex; justify-content: flex-end; margin-bottom: 6mm; }
  .totals-box { width: 220px; }
  .tot-row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #f0f0f0; font-size: 12px; }
  .tot-row.grand { font-size: 15px; font-weight: 700; color: #2e9f86; border-top: 2px solid #2e9f86; border-bottom: none; padding-top: 6px; }
  .tot-row.balance { color: #dc2626; font-weight: 700; }
  .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin-bottom: 4px; margin-top: 6mm; }
  .pay-table thead tr { background: #374151; }
  .tafqeet { direction: rtl; border: 1.5px solid #2e9f86; border-radius: 6px; padding: 8px 14px; margin-top: 6mm; display: flex; gap: 10px; align-items: flex-start; background: #f0fdf9; }
  .tafqeet-label { font-size: 11px; font-weight: 700; color: #2e9f86; white-space: nowrap; margin-top: 2px; }
  .tafqeet-text { font-size: 12.5px; font-weight: 600; color: #111; line-height: 1.6; }
  .footer { margin-top: 8mm; text-align: center; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 6mm; }
  @media print {
    body { padding: 12mm; }
    @page { size: A4; margin: 0; }
  }
</style></head>
<body>

  <!-- Header: logo + company info -->
  <div class="header">
    <div class="company-info">
      <h1>${escapeHtml(company?.name || 'MMM POS')}</h1>
      ${company?.address    ? `<p>${escapeHtml(company.address)}</p>` : ''}
      ${company?.phone      ? `<p>Tel: ${escapeHtml(company.phone)}</p>` : ''}
      ${company?.email      ? `<p>${escapeHtml(company.email)}</p>` : ''}
      ${company?.tax_number ? `<p>Tax No: ${escapeHtml(company.tax_number)}</p>` : ''}
    </div>
    ${logoSrc ? `<img class="logo" src="${logoSrc}" alt="logo" />` : ''}
  </div>

  <!-- Doc info bar -->
  <div class="doc-info">
    <div class="doc-info-block">
      <div class="label">Document</div>
      <div class="value doc-number">${escapeHtml(doc?.number || '—')}</div>
      <div style="font-size:11px;color:#6b7280;margin-top:2px">${escapeHtml(docType)}</div>
    </div>
    <div class="doc-info-block">
      <div class="label">Date</div>
      <div class="value">${docDate}</div>
    </div>
    <div class="doc-info-block">
      <div class="label">Customer / Account</div>
      <div class="value">${escapeHtml(account?.name || '—')}</div>
      ${account?.phone ? `<div style="font-size:11px;color:#6b7280">${escapeHtml(account.phone)}</div>` : ''}
    </div>
    ${doc?.notes ? `<div class="doc-info-block"><div class="label">Notes</div><div class="value" style="font-weight:400">${escapeHtml(doc.notes)}</div></div>` : ''}
  </div>

  <!-- Items -->
  <table>
    <thead><tr>
      <th>Product</th>
      <th class="center">Qty</th>
      <th class="right">Price</th>
      ${hasTax      ? '<th class="right">Tax</th>'      : ''}
      ${hasDiscount ? '<th class="right">Discount</th>' : ''}
      <th class="right">Total</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
  </table>

  <!-- Totals -->
  <div class="totals-section">
    <div class="totals-box">
      <div class="tot-row"><span>Subtotal</span><span>$${money(totals?.subtotal)}</span></div>
      ${hasTax      ? `<div class="tot-row"><span>Tax</span><span>$${money(totals?.tax)}</span></div>` : ''}
      ${hasDiscount ? `<div class="tot-row"><span>Discount</span><span>-$${money(totals?.discount)}</span></div>` : ''}
      ${showLBP     ? `<div class="tot-row"><span>Total LBP</span><span>${lbpFmt(Math.round((totals?.total || 0) * rate))} ل.ل</span></div>` : ''}
      <div class="tot-row grand"><span>Total USD</span><span>$${money(totals?.total)}</span></div>
      <div class="tot-row"><span>Paid</span><span>$${money(totals?.paid)}</span></div>
      ${(totals?.balance || 0) > 0.005 ? `<div class="tot-row balance"><span>Balance Due</span><span>$${money(totals?.balance)}</span></div>` : '<div class="tot-row" style="color:#065f46"><span>Balance</span><span>Paid ✓</span></div>'}
    </div>
  </div>

  <!-- Payments -->
  ${paymentRows ? `
  <div class="section-title">Payments</div>
  <table class="pay-table">
    <thead><tr>
      <th>Method</th><th>Date</th><th class="right">Amount</th><th>Notes</th>
    </tr></thead>
    <tbody>${paymentRows}</tbody>
  </table>` : ''}

  <!-- Tafqeet -->
  ${totals?.total ? `
  <div class="tafqeet">
    <span class="tafqeet-label">المبلغ كتابةً:</span>
    <span class="tafqeet-text">${escapeHtml(tafqeetUSD(totals.total))}${showLBP && rate ? `<br><span style="font-size:11px;color:#6b7280">${escapeHtml(tafqeetLBP(Math.round((totals.total) * rate)))}</span>` : ''}</span>
  </div>` : ''}

  <!-- Footer -->
  <div class="footer">${escapeHtml(company?.receipt_footer || 'Thank you for your business!')}</div>

  <script>window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 400); }</script>
</body></html>`;

  openPrint(html, 'invoice_a4');
}

/**
 * openPrint — send HTML to printer.
 * On local installs: POST to /api/print (silent, uses configured printer).
 * If no printer is configured for this job type, falls back to window.print().
 * On cloud: always uses window.print() popup.
 *
 * @param {string} html       - Full HTML document to print
 * @param {string} [jobType]  - 'receipt_80mm' | 'invoice_a4' | 'kitchen' | 'report'
 */
async function openPrint(html, jobType) {
  if (isLocalInstall && jobType) {
    try {
      await api.post('/api/print', { html, job_type: jobType });
      return; // printed silently — done
    } catch (e) {
      const err = e.response?.data?.error;
      if (err !== 'no_printer_configured') {
        // Unexpected error — show alert but still fall through to popup
        console.error('Silent print failed:', err || e.message);
      }
      // If no_printer_configured or any error → fall through to popup below
    }
  }
  // Popup fallback (cloud, or no printer configured for this job type)
  const w = window.open('', '_blank', 'width=800,height=700');
  if (w) { w.document.write(html); w.document.close(); }
}